import React from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import { Role, PanelSection, RoleDefaultAccess } from '../../../../../shared/services/userService';

interface RoleDefaultAccessModalProps {
  role: Role | null;
  roleDefaultAccess: RoleDefaultAccess[];
  panelSections: PanelSection[];
  updating: boolean;
  getSectionAccess: (sectionId: string) => boolean;
  onToggleAccess: (section: string, hasAccess: boolean) => void;
  onClose: () => void;
}

const RoleDefaultAccessModal: React.FC<RoleDefaultAccessModalProps> = ({
  role,
  roleDefaultAccess,
  panelSections,
  updating,
  getSectionAccess,
  onToggleAccess,
  onClose
}) => {
  if (!role) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4">
          Gérer les accès par défaut - {role.role_name}
        </h3>
        <p className="text-gray-400 mb-6 text-sm">
          Définissez les accès par défaut pour ce rôle (hérités par tous les comptes de ce rôle). Les surcharges individuelles définies dans la fiche d’un joueur (accès panel) priment toujours sur ces valeurs pour ce joueur.
        </p>
        
        {panelSections.length === 0 ? (
          <p className="text-gray-400">Chargement des sections...</p>
        ) : (
          <div className="space-y-3">
            {panelSections.map((section) => {
              const hasAccess = getSectionAccess(section.id);
              return (
                <div
                  key={section.id}
                  className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">{section.name}</h4>
                    <p className="text-gray-400 text-sm mt-1">{section.description}</p>
                    {roleDefaultAccess.find(a => a.section === section.id) && (
                      <p className="text-gray-500 text-xs mt-1">
                        Modifié le: {new Date(roleDefaultAccess.find(a => a.section === section.id)!.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      hasAccess
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-red-500/20 text-red-400 border border-red-500/50'
                    }`}>
                      {hasAccess ? 'Accès par défaut' : 'Pas d\'accès par défaut'}
                    </span>
                    <button
                      onClick={() => onToggleAccess(section.id, !hasAccess)}
                      disabled={updating}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        hasAccess
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {updating ? '...' : hasAccess ? 'Retirer l\'accès par défaut' : 'Accorder l\'accès par défaut'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
        >
          Fermer
        </button>
      </Card>
    </div>,
    document.body
  );
};

export default RoleDefaultAccessModal;

